import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigation } from 'react-router';

const UserSearchQueryContext = createContext<{
  hideSearchSuggestions: (persist?: boolean) => void;
  searchModalQuery: string;
  searchResultsQuery: string;
  setSearchModalQuery: (query: string) => void;
  setSearchResultsQuery: (query: string) => void;
  shouldOpenSearchModal: boolean;
  showSearchSuggestions: (persist?: boolean) => void;
  transition: string;
}>({
  hideSearchSuggestions: () => null,
  searchModalQuery: '',
  searchResultsQuery: '',
  setSearchModalQuery: () => null,
  setSearchResultsQuery: () => null,
  shouldOpenSearchModal: false,
  showSearchSuggestions: () => null,
  transition: '',
});

export function UserSearchQueryProvider({ children }: PropsWithChildren) {
  const [searchModalQuery, setSearchModalQuery] = useState(''); // for the search modal (in header)
  const [shouldOpenSearchModal, setShouldOpenSearchModal] = useState(false);
  const [transition, setTransition] = useState('');
  const [searchResultsQuery, setSearchResultsQuery] = useState(''); // for the search results page
  const { location: destinationLocation, state } = useNavigation();
  const isNavigating = state === 'loading';
  const navigatingToSearchResults =
    destinationLocation?.pathname.match(/search$/);

  // TODO: Remove transition class logic when c2CustomComponents is fully rolled out.
  // The new SearchDropdown uses CSS module classes instead of global transition classes.
  const hideSearchSuggestions = useCallback(() => {
    setTransition('search-suggestions--on-exit');
    setTimeout(() => {
      setShouldOpenSearchModal(false);
      setTransition('search-suggestions--hide');
    }, 500);
  }, []);

  const showSearchSuggestions = useCallback(() => {
    setShouldOpenSearchModal(true);
    setTimeout(() => {
      setTransition('search-suggestions--on-enter');
    }, 10);
  }, []);

  useEffect(() => {
    if (isNavigating && !navigatingToSearchResults) {
      setSearchModalQuery('');
    }
  }, [isNavigating, navigatingToSearchResults, setSearchModalQuery]);

  const value = useMemo(
    () => ({
      hideSearchSuggestions,
      searchModalQuery,
      searchResultsQuery,
      setSearchModalQuery,
      setSearchResultsQuery,
      shouldOpenSearchModal,
      showSearchSuggestions,
      transition,
    }),
    [
      hideSearchSuggestions,
      searchModalQuery,
      searchResultsQuery,
      shouldOpenSearchModal,
      showSearchSuggestions,
      transition,
    ]
  );

  return (
    <UserSearchQueryContext.Provider value={value}>
      {children}
    </UserSearchQueryContext.Provider>
  );
}

export const useUserSearchQuery = () => useContext(UserSearchQueryContext);
